import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, CalendarClock } from "lucide-react";
import { useApi, errMsg } from "@/lib/api";
import { fmtDT, inr } from "@/lib/brand";
import { DateTimeField } from "@/components/site/DateTimeField";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { openCashfree } from "@/pages/Checkout";

const local = (iso) => new Date(iso).toLocaleString("sv-SE", { timeZone: "Asia/Kolkata" }).replace(" ", "T").slice(0, 16);
const addHours = (s, h) => local(new Date(new Date(`${s}:00+05:30`).getTime() + h * 3600000).toISOString());
const field = "mt-1 w-full rounded-xl border border-hc-sage bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-hc-green";

function Summary({ plan }) {
  if (!plan) return null;
  return (
    <div className="space-y-2 rounded-xl bg-hc-mint/60 p-4 text-sm" data-testid="change-summary">
      {plan.kind === "extension" && <p className="flex justify-between"><span>Extra time</span><b>{plan.extra_hours} hrs</b></p>}
      {plan.amount > 0 && <p className="flex justify-between"><span>Pay now (incl. 18% GST)</span><b className="tabular text-hc-green" data-testid="change-amount">{inr(plan.amount)}</b></p>}
      {plan.refund > 0 && <p className="flex justify-between"><span>Refund to you</span><b className="tabular text-hc-success" data-testid="change-refund">{inr(plan.refund)}</b></p>}
      {plan.amount === 0 && !plan.refund && <p data-testid="change-free">No extra charge for this change.</p>}
      {!plan.available && <p className="font-semibold text-hc-error" data-testid="change-unavailable">The car is already booked for part of these dates. Try other times.</p>}
    </div>
  );
}

export function ChangeDialog({ b, kind, open, onOpenChange, onDone }) {
  const api = useApi();
  const ext = kind === "extension";
  const [pickup, setPickup] = useState(local(b.pickup_at));
  const [ret, setRet] = useState(addHours(local(b.return_at), ext ? 4 : 0));
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const path = ext ? "extend" : "reschedule";
  const body = ext ? { return_at: ret } : { pickup_at: pickup, return_at: ret };
  useEffect(() => {
    if (!open) return;
    setPlan(null);
    setError("");
    if (!ext && pickup === local(b.pickup_at) && ret === local(b.return_at)) return;
    api.post(`/bookings/${b.id}/${path}/quote`, body).then((r) => setPlan(r.data)).catch((e) => setError(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pickup, ret]);
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${b.id}/${path}`, body);
      if (r.data.applied) {
        toast.success(ext ? "Trip extended" : "Booking rescheduled");
        onOpenChange(false);
        onDone();
      } else await openCashfree(r.data.payment_session_id, r.data.mode);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`change-dialog-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{ext ? <CalendarPlus className="h-5 w-5 text-hc-green" /> : <CalendarClock className="h-5 w-5 text-hc-green" />}{ext ? "Extend your trip" : "Reschedule booking"}</DialogTitle>
          <DialogDescription>{ext ? `Current return: ${fmtDT(b.return_at)}. We check the car is free, then you pay only the extra time.` : "You can reschedule once, up to 24 hours before pickup. Price is recalculated for the new dates; any difference is charged or refunded."}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          {!ext && <label className="text-xs font-semibold uppercase tracking-wide text-hc-slate">New pickup<DateTimeField testId="change-pickup" value={pickup} onChange={setPickup} className={field} /></label>}
          <label className="text-xs font-semibold uppercase tracking-wide text-hc-slate">{ext ? "New return" : "New return"}<DateTimeField testId="change-return" value={ret} onChange={setRet} className={field} /></label>
        </div>
        {error && <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="change-error">{error}</p>}
        {!ext && !plan && !error && <p className="text-sm text-hc-slate" data-testid="change-hint">Pick your new pickup and return times to see the price.</p>}
        <Summary plan={plan} />
        <button onClick={confirm} disabled={!plan || !plan.available || busy} className="btn-primary w-full" data-testid="change-confirm">
          {busy ? "Please wait…" : plan?.amount > 0 ? `Pay ${inr(plan.amount)} & confirm` : "Confirm change"}
        </button>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeButtons({ b, onDone }) {
  const [kind, setKind] = useState(null);
  const canExtend = ["CONFIRMED", "ACTIVE"].includes(b.status);
  const canReschedule = b.status === "CONFIRMED" && !b.reschedule_count && new Date(b.pickup_at) - Date.now() >= 24 * 3600000;
  if (!canExtend && !canReschedule) return null;
  const cls = "rounded-full border border-hc-sage px-4 py-2 text-sm font-semibold transition-colors duration-200 hover:border-hc-green hover:text-hc-green";
  return (
    <>
      {canExtend && <button onClick={() => setKind("extension")} className={cls} data-testid={`booking-extend-${b.id}`}>Extend</button>}
      {canReschedule && <button onClick={() => setKind("reschedule")} className={cls} data-testid={`booking-reschedule-${b.id}`}>Reschedule</button>}
      {kind && <ChangeDialog b={b} kind={kind} open onOpenChange={(o) => !o && setKind(null)} onDone={onDone} />}
    </>
  );
}
